import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, SquarePen, Ban, RotateCcw, AlertTriangle, RefreshCw,
  Phone, MapPin, Calendar, CreditCard, DollarSign,
  AlertCircle, Shield, FileText, CheckCircle2,
  ChevronRight, Building2, User, History
} from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import EstadoDialog from '../components/EstadoDialog';
import { useToast } from '../components/ToastProvider';
import { clientesAPI, describirError } from '../services/api';
import { formatoTelefono } from '../utils/finanzas';
import '../styles/Pages.css';
import '../styles/Expediente.css';
import { etiquetaFrecuencia } from '../utils/historicos';

const TABS = [
  { id: 'resumen', label: 'Resumen' },
  { id: 'creditos', label: 'Créditos' },
  { id: 'pagos', label: 'Pagos' },
  { id: 'cobranza', label: 'Cobranza' },
  { id: 'documentos', label: 'Documentos' },
  { id: 'auditoria', label: 'Auditoría' }
];

const formatoMoneda = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

const MESES_ABR = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

const fechaCorta = (iso) => {
  if (!iso) return '—';
  if (typeof iso === 'string') {
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = match[1];
      const monthIdx = parseInt(match[2], 10) - 1;
      const day = match[3];
      if (monthIdx >= 0 && monthIdx < 12) {
        return `${day} ${MESES_ABR[monthIdx]} ${year}`;
      }
    }
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).toUpperCase();
};

const fechaHora = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const iniciales = (n = '') =>
  n.trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';

function ClienteExpediente() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [expediente, setExpediente] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('resumen');
  const [dialogo, setDialogo] = useState(null);
  const [aplicando, setAplicando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const expRes = await clientesAPI.expedienteIntegral(id).catch(() => clientesAPI.obtener(id));

      // Handle unified payload structure
      if (expRes.data && expRes.data.cliente) {
        setExpediente(expRes.data);
      } else {
        // Fallback for direct client object
        setExpediente({
          cliente: expRes.data,
          resumenFinanciero: {
            creditosTotales: 0,
            creditosConSaldo: 0,
            saldoActual: 0,
            saldoVencidoReal: 0,
            saldoPorVencer: 0,
            diasAtrasoMax: 0,
            documentosVencidos: 0,
            modalidades: [],
            frecuencias: []
          },
          creditos: [],
          pagos: [],
          cobranza: { diasAtrasoMax: 0, documentosVencidos: 0, saldoVencidoReal: 0, gestiones: [], promesas: [] },
          documentos: []
        });
      }
    } catch (e) {
      setError(describirError(e));
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const aplicarEstado = async (motivo) => {
    setAplicando(true);
    try {
      await clientesAPI.cambiarEstado(id, dialogo.destino, motivo);
      toast.exito(dialogo.destino === 'BLOQUEADO' ? 'Cliente bloqueado.' : 'Cliente reactivado.');
      setDialogo(null);
      await cargar();
    } catch (e) {
      toast.error(describirError(e).mensaje);
    } finally {
      setAplicando(false);
    }
  };

  if (cargando) {
    return (
      <div className="page-container">
        <div className="loading" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '4rem 0' }}>
          <RefreshCw className="spin" size={28} />
          <span>Cargando expediente integral…</span>
        </div>
      </div>
    );
  }

  if (error || !expediente) {
    return (
      <div className="page-container">
        <div className="estado-vacio">
          <span className="estado-vacio-icono is-error">
            <AlertTriangle size={20} strokeWidth={1.75} />
          </span>
          <p className="estado-vacio-titulo">No fue posible cargar el expediente.</p>
          <p className="estado-vacio-texto">{error?.mensaje || 'Error al obtener datos.'}</p>
          <button className="btn btn-secondary" onClick={cargar}>
            <RefreshCw size={15} strokeWidth={1.75} />
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const { cliente, resumenFinanciero, creditos, pagos, cobranza, documentos } = expediente;
  const d = cliente.domicilio || {};
  const bloqueado = cliente.estado === 'BLOQUEADO';

  // Compute pending KYC capture items without inferring unstructured fields
  const pendientesCaptura = [];
  if (!expediente.identidadOculta && !cliente.curp) pendientesCaptura.push('CURP');
  if (!expediente.identidadOculta && !cliente.identificacionPersonal) pendientesCaptura.push('Identificación Oficial (INE)');
  if (!cliente.email) pendientesCaptura.push('Correo electrónico');
  if (!cliente.telefonoAlterno) pendientesCaptura.push('Teléfono alterno');
  if (!d.colonia) pendientesCaptura.push('Colonia estructurada');
  if (!d.localidad) pendientesCaptura.push('Localidad estructurada');

  return (
    <div className="page-container">
      {/* Botón Volver */}
      <button className="btn btn-ghost volver" onClick={() => navigate('/clientes')} style={{ marginBottom: '0.75rem' }}>
        <ArrowLeft size={16} strokeWidth={1.75} />
        Volver a clientes
      </button>

      {/* 1. CABECERA COMPACTA DEL CLIENTE */}
      <header className="exp-header-compact">
        <div className="exp-avatar-wrap">
          <span className="exp-avatar-badge">{iniciales(cliente.nombreCompleto)}</span>
        </div>

        <div className="exp-main-info">
          <div className="exp-title-row">
            <h2 className="exp-client-name">{cliente.nombreCompleto}</h2>
            <StatusBadge value={cliente.estado} />
            {cliente.esSeedDev && <span className="exp-seed-pill">SEED DEV</span>}
          </div>

          <div className="exp-badges-meta">
            <span className="exp-meta-item">
              <strong className="exp-meta-label">FOLIO:</strong> {cliente.folio}
            </span>
            {cliente.rfc && (
              <span className="exp-meta-item">
                <strong className="exp-meta-label">RFC:</strong> {cliente.rfc}
              </span>
            )}
            <span className="exp-meta-item">
              <Phone size={13} strokeWidth={2} className="exp-inline-icon" /> {formatoTelefono(cliente.telefonoPrincipal)}
            </span>
            {resumenFinanciero.agrupacion && (
              <span className="exp-meta-item exp-agrupacion-pill">
                <Building2 size={13} strokeWidth={2} className="exp-inline-icon" />
                {resumenFinanciero.agrupacion.tipo}: {resumenFinanciero.agrupacion.nombre} ({resumenFinanciero.agrupacion.folio})
              </span>
            )}
          </div>

          {bloqueado && cliente.motivoEstado && (
            <p className="exp-bloqueo-alerta">
              <AlertCircle size={14} /> Motivo del bloqueo: {cliente.motivoEstado}
            </p>
          )}
        </div>

        <div className="exp-header-actions">
          <button className="btn btn-secondary" onClick={() => navigate(`/clientes/${id}/editar`)}>
            <SquarePen size={15} strokeWidth={1.75} />
            Editar
          </button>
          {bloqueado ? (
            <button className="btn btn-primary" onClick={() => setDialogo({ destino: 'ACTIVO' })}>
              <RotateCcw size={15} strokeWidth={1.75} />
              Reactivar
            </button>
          ) : (
            <button className="btn btn-danger" onClick={() => setDialogo({ destino: 'BLOQUEADO' })}>
              <Ban size={15} strokeWidth={1.75} />
              Bloquear
            </button>
          )}
        </div>
      </header>

      {/* 2. BARRA DE RESUMEN FINANCIERO */}
      <section className="exp-kpi-bar" aria-label="Resumen Financiero del Cliente">
        <div className="exp-kpi-item">
          <span className="exp-kpi-subhead">CRÉDITOS</span>
          <div className="exp-kpi-mainval">
            {resumenFinanciero.creditosTotales} <span className="exp-kpi-denom">total</span>
          </div>
          <span className="exp-kpi-footer">
            {resumenFinanciero.creditosConSaldo} con saldo
          </span>
        </div>

        <div className="exp-kpi-item">
          <span className="exp-kpi-subhead">SALDO ACTUAL</span>
          <div className="exp-kpi-mainval text-teal">
            {formatoMoneda(resumenFinanciero.saldoActual)}
          </div>
          <span className="exp-kpi-footer">Cartera viva</span>
        </div>

        <div className="exp-kpi-item">
          <span className="exp-kpi-subhead">SALDO VENCIDO REAL</span>
          <div className={`exp-kpi-mainval ${resumenFinanciero.saldoVencidoReal > 0 ? 'text-danger' : 'text-success'}`}>
            {formatoMoneda(resumenFinanciero.saldoVencidoReal)}
          </div>
          <span className="exp-kpi-footer">
            {resumenFinanciero.saldoVencidoReal > 0 ? `${resumenFinanciero.documentosVencidos} cuotas vencidas` : 'Al corriente'}
          </span>
        </div>

        <div className="exp-kpi-item">
          <span className="exp-kpi-subhead">SALDO POR VENCER</span>
          <div className="exp-kpi-mainval">
            {formatoMoneda(resumenFinanciero.saldoPorVencer)}
          </div>
          <span className="exp-kpi-footer">Vigente a futuro</span>
        </div>

        <div className="exp-kpi-item">
          <span className="exp-kpi-subhead">DÍAS DE ATRASO</span>
          <div className={`exp-kpi-mainval ${resumenFinanciero.diasAtrasoMax > 0 ? 'text-danger' : 'text-success'}`}>
            {resumenFinanciero.diasAtrasoMax} <span className="exp-kpi-denom">días</span>
          </div>
          <span className="exp-kpi-footer">Desde el vencimiento pendiente más antiguo</span>
        </div>

        <div className="exp-kpi-item">
          <span className="exp-kpi-subhead">MODALIDAD / FREC.</span>
          <div className="exp-kpi-mainval" style={{ fontSize: '0.9375rem', fontWeight: 600 }}>
            {resumenFinanciero.modalidades.join(', ') || 'PERSONAL'}
          </div>
          <span className="exp-kpi-footer">
            {resumenFinanciero.frecuencias.join(', ') || 'QUINCENAL'}
          </span>
        </div>
      </section>

      {/* 3. NAVEGACIÓN DE PESTAÑAS */}
      <nav className="exp-tabs-nav" role="tablist">
        {TABS.filter(t => t.id !== 'documentos' || !expediente.identidadOculta).map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`exp-tab-btn ${tab === t.id ? 'is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.id === 'creditos' && creditos.length > 0 && (
              <span className="exp-tab-counter">{creditos.length}</span>
            )}
            {t.id === 'pagos' && pagos.length > 0 && (
              <span className="exp-tab-counter">{pagos.length}</span>
            )}
            {t.id === 'documentos' && documentos.length > 0 && (
              <span className="exp-tab-counter">{documentos.length}</span>
            )}
          </button>
        ))}
      </nav>

      {/* 4. CONTENIDO DE PESTAÑAS */}

      {/* --- TAB 1: RESUMEN --- */}
      {tab === 'resumen' && (
        <div className="exp-sections-grid">
          {/* Tarjeta Identificación y Contacto */}
          <div className="exp-card">
            <div className="exp-card-header">
              <User size={16} className="exp-card-icon" />
              <h3>Identificación y Contacto</h3>
            </div>
            <div className="exp-card-body">
              <dl className="exp-dl-grid">
                <div>
                  <dt>RFC</dt>
                  <dd className="exp-dd-highlight">{expediente.identidadOculta ? 'Acceso restringido' : (cliente.rfc || 'No registrado')}</dd>
                </div>
                <div>
                  <dt>Teléfono Principal</dt>
                  <dd>{cliente.telefonoPrincipal || 'No registrado'}</dd>
                </div>
                {cliente.telefonoAlterno && (
                  <div>
                    <dt>Teléfono Alterno</dt>
                    <dd>{cliente.telefonoAlterno}</dd>
                  </div>
                )}
                {cliente.email && (
                  <div>
                    <dt>Correo Electrónico</dt>
                    <dd>{cliente.email}</dd>
                  </div>
                )}
                {cliente.curp && (
                  <div>
                    <dt>CURP</dt>
                    <dd>{cliente.curp}</dd>
                  </div>
                )}
                {cliente.identificacionPersonal && (
                  <div>
                    <dt>Identificación</dt>
                    <dd>{cliente.identificacionPersonal}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Tarjeta Domicilio */}
          <div className="exp-card">
            <div className="exp-card-header">
              <MapPin size={16} className="exp-card-icon" />
              <h3>Domicilio</h3>
            </div>
            <div className="exp-card-body">
              {d.calle || d.municipio || d.estado || d.codigoPostal ? (
                <div className="exp-address-block">
                  <div className="exp-address-street">{d.calle || 'Dirección registrada'}</div>
                  <div className="exp-address-locality">
                    {[d.municipio, d.estado].filter(Boolean).join(', ')}
                  </div>
                  <div className="exp-address-meta">
                    {d.codigoPostal && <span className="exp-cp-badge">CP {d.codigoPostal}</span>}
                    {d.colonia && <span className="exp-locality-tag">Col. {d.colonia}</span>}
                    {d.localidad && <span className="exp-locality-tag">Loc. {d.localidad}</span>}
                    <span className="exp-locality-tag">{d.pais || 'MÉXICO'}</span>
                  </div>
                </div>
              ) : (
                <p className="exp-empty-text">Sin domicilio registrado.</p>
              )}
            </div>
          </div>

          {/* Tarjeta Historial y Situación Operativa */}
          <div className="exp-card">
            <div className="exp-card-header">
              <Calendar size={16} className="exp-card-icon" />
              <h3>Historial Operativo</h3>
            </div>
            <div className="exp-card-body">
              <dl className="exp-dl-grid">
                <div>
                  <dt>Primer Crédito Registrado</dt>
                  <dd className="exp-dd-highlight">
                    {cliente.fechas?.primerCreditoRegistrado
                      ? fechaCorta(cliente.fechas.primerCreditoRegistrado)
                      : 'Sin créditos históricos'}
                  </dd>
                </div>
                <div>
                  <dt>Total Créditos Otorgados</dt>
                  <dd>{resumenFinanciero.creditosTotales}</dd>
                </div>
                <div>
                  <dt>Créditos con Saldo</dt>
                  <dd>{resumenFinanciero.creditosConSaldo}</dd>
                </div>
                <div>
                  <dt>Situación de Cartera</dt>
                  <dd>
                    {resumenFinanciero.saldoVencidoReal > 0 ? (
                      <span className="text-danger font-semibold">Con atraso ({resumenFinanciero.diasAtrasoMax} días)</span>
                    ) : (
                      <span className="text-success font-semibold">Al corriente</span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Tarjeta Información Pendiente de Captura (Discreta) */}
          {pendientesCaptura.length > 0 && (
            <div className="exp-card exp-card-pending">
              <div className="exp-card-header">
                <CheckCircle2 size={15} className="exp-card-icon text-muted" />
                <h3 style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                  Información Pendiente de Captura
                </h3>
              </div>
              <div className="exp-card-body" style={{ padding: '0.75rem 1rem' }}>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                  Información no disponible en el sistema anterior. Puede completarse posteriormente.
                </p>
                <div className="exp-pending-tags">
                  {pendientesCaptura.map((p) => (
                    <span key={p} className="exp-pending-chip">{p}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- TAB 2: CRÉDITOS --- */}
      {tab === 'creditos' && (
        <div className="exp-panel-table">
          <div className="exp-table-header">
            <h3>Créditos del Cliente ({creditos.length})</h3>
            <p>Historial completo de contratos, modalidades y saldos vigentes.</p>
          </div>

          {creditos.length === 0 ? (
            <div className="estado-vacio">
              <span className="estado-vacio-icono"><CreditCard size={20} strokeWidth={1.75} /></span>
              <p className="estado-vacio-texto">El cliente no tiene créditos registrados.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>Estado</th>
                    <th>Modalidad</th>
                    <th>Frecuencia</th>
                    <th>Monto Colocado</th>
                    <th>Saldo Actual</th>
                    <th>Saldo Vencido</th>
                    <th>Días Atraso</th>
                    <th>Desembolso</th>
                    <th>Vencimiento</th>
                    <th style={{ textAlign: 'right' }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {creditos.map((c) => (
                    <tr key={c.id}>
                      <td className="font-semibold text-primary">{c.folio}</td>
                      <td><StatusBadge value={c.estado} /></td>
                      <td>{c.tipoCredito}</td>
                      <td>{etiquetaFrecuencia(c, '')}</td>
                      <td>{formatoMoneda(c.montoSolicitado)}</td>
                      <td className="font-semibold">{formatoMoneda(c.saldoActual)}</td>
                      <td className={c.saldoVencido > 0 ? 'text-danger font-semibold' : ''}>
                        {formatoMoneda(c.saldoVencido)}
                      </td>
                      <td>
                        {c.diasAtraso > 0 ? (
                          <span className="text-danger font-semibold">{c.diasAtraso} d</span>
                        ) : (
                          <span className="text-success">0 d</span>
                        )}
                      </td>
                      <td>{fechaCorta(c.fechaDesembolso)}</td>
                      <td>{fechaCorta(c.fechaVencimientoFinal)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => navigate(`/creditos/${c.id}`)}
                          title="Ver detalle del crédito"
                        >
                          Ver ficha <ChevronRight size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* --- TAB 3: PAGOS --- */}
      {tab === 'pagos' && (
        <div className="exp-panel-table">
          <div className="exp-table-header">
            <h3>Historial de Pagos ({pagos.length})</h3>
            <p>Registro de pagos y abonos aplicados a las cuentas del cliente (Solo Lectura).</p>
          </div>

          {pagos.length === 0 ? (
            <div className="estado-vacio">
              <span className="estado-vacio-icono"><DollarSign size={20} strokeWidth={1.75} /></span>
              <p className="estado-vacio-texto">No hay pagos registrados para este cliente.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Fecha Pago</th>
                    <th>Folio Pago</th>
                    <th>Crédito</th>
                    <th>Importe Recibido</th>
                    <th>Importe Aplicado</th>
                    <th>Forma de Pago</th>
                    <th>Referencia</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {pagos.map((p) => (
                    <tr key={p.id}>
                      <td>{fechaCorta(p.fechaPago)}</td>
                      <td className="font-semibold text-primary">{p.folioPago}</td>
                      <td>{p.creditoFolio || '—'}</td>
                      <td className="font-semibold">{formatoMoneda(p.importeRecibido)}</td>
                      <td>{formatoMoneda(p.importeAplicado)}</td>
                      <td>{p.formaPago}</td>
                      <td className="text-muted">{p.referencia || '—'}</td>
                      <td><StatusBadge value={p.estado} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* --- TAB 4: COBRANZA --- */}
      {tab === 'cobranza' && (
        <div className="exp-panel-table">
          <div className="exp-table-header">
            <h3>Situación de Cobranza</h3>
            <p>Gestiones, promesas de pago y registro preventivo de cartera en mora.</p>
          </div>

          <div className="exp-cobranza-kpis" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', padding: '1rem 1.25rem' }}>
            <div className="exp-kpi-card">
              <span className="exp-kpi-label">DÍAS ATRASO MÁXIMO</span>
              <div className={`exp-kpi-val ${cobranza.diasAtrasoMax > 0 ? 'text-danger' : 'text-success'}`}>
                {cobranza.diasAtrasoMax} días
              </div>
            </div>
            <div className="exp-kpi-card">
              <span className="exp-kpi-label">DOCUMENTOS VENCIDOS</span>
              <div className={`exp-kpi-val ${cobranza.documentosVencidos > 0 ? 'text-danger' : 'text-success'}`}>
                {cobranza.documentosVencidos} cuotas
              </div>
            </div>
            <div className="exp-kpi-card">
              <span className="exp-kpi-label">SALDO VENCIDO REAL</span>
              <div className={`exp-kpi-val ${cobranza.saldoVencidoReal > 0 ? 'text-danger' : 'text-success'}`}>
                {formatoMoneda(cobranza.saldoVencidoReal)}
              </div>
            </div>
          </div>

          <div style={{ padding: '0 1.25rem 1.25rem 1.25rem' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
              Gestiones Registradas ({cobranza.gestiones.length})
            </h4>
            {cobranza.gestiones.length === 0 ? (
              <p className="exp-empty-text">Sin gestiones de cobranza registradas en el periodo.</p>
            ) : (
              <ul className="exp-cobranza-list">
                {cobranza.gestiones.map((g) => (
                  <li key={g.id} className="exp-cobranza-item">
                    <span className="exp-cobranza-date">{fechaCorta(g.fechaGestion)}</span>
                    <span className="exp-cobranza-type">{g.tipoGestion}</span>
                    <span className="exp-cobranza-result">{g.resultado}</span>
                    <span className="exp-cobranza-notes">{g.observaciones}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* --- TAB 5: DOCUMENTOS --- */}
      {tab === 'documentos' && !expediente.identidadOculta && (
        <div className="exp-panel-table">
          <div className="exp-table-header">
            <h3>Expediente Digital y Documentación Contractual ({documentos.length})</h3>
            <p>Visualización y gestión de contratos, pagarés y expedientes digitalizados.</p>
          </div>

          {documentos.length === 0 ? (
            <div className="estado-vacio">
              <span className="estado-vacio-icono"><FileText size={20} strokeWidth={1.75} /></span>
              <p className="estado-vacio-texto">No hay documentos digitalizados generados para este cliente.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Documento</th>
                    <th>Código</th>
                    <th>Crédito</th>
                    <th>Versión</th>
                    <th>Fecha Emisión</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {documentos.map((d) => (
                    <tr key={d.id}>
                      <td className="font-semibold text-primary">{d.nombre}</td>
                      <td>{d.codigo}</td>
                      <td>{d.creditoFolio || '—'}</td>
                      <td>v{d.version}</td>
                      <td>{fechaHora(d.createdAt)}</td>
                      <td><StatusBadge value={d.estado} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* --- TAB 6: AUDITORÍA (SOLO ADMIN / TRAZABILIDAD TÉCNICA) --- */}
      {tab === 'auditoria' && (
        <div className="exp-sections-grid">
          {/* Trazabilidad Legacy */}
          <div className="exp-card">
            <div className="exp-card-header">
              <History size={16} className="exp-card-icon" />
              <h3>Procedencia de Migración Legacy</h3>
            </div>
            <div className="exp-card-body">
              {cliente.auditoria?.legacyRefs?.length > 0 ? (
                <div className="exp-legacy-list">
                  {cliente.auditoria.legacyRefs.map((ref) => (
                    <dl key={ref.id} className="exp-dl-grid" style={{ marginBottom: '1rem', borderBottom: '1px dashed var(--border)', paddingBottom: '0.75rem' }}>
                      <div>
                        <dt>Legacy Cliente ID</dt>
                        <dd className="font-semibold text-primary">{ref.legacyClienteId}</dd>
                      </div>
                      <div>
                        <dt>Sistema Fuente</dt>
                        <dd>{ref.sourceSystem}</dd>
                      </div>
                      <div>
                        <dt>Lote Migración</dt>
                        <dd className="text-muted" style={{ fontSize: '0.75rem' }}>{ref.migracionLoteId}</dd>
                      </div>
                      <div>
                        <dt>Fecha Materialización</dt>
                        <dd>{fechaHora(ref.createdAt)}</dd>
                      </div>
                    </dl>
                  ))}
                </div>
              ) : (
                <p className="exp-empty-text">Cliente originado directamente en MIDAS (Sin referencias legacy).</p>
              )}
            </div>
          </div>

          {/* Metadatos del Sistema y Cuentas Contables */}
          <div className="exp-card">
            <div className="exp-card-header">
              <Shield size={16} className="exp-card-icon" />
              <h3>Metadatos y Parámetros Técnicos</h3>
            </div>
            <div className="exp-card-body">
              <dl className="exp-dl-grid">
                <div>
                  <dt>Tipo de Persona</dt>
                  <dd>{cliente.auditoria?.tipoPersona || 'PERSONA_FISICA'}</dd>
                </div>
                <div>
                  <dt>Fecha Técnica Inserción</dt>
                  <dd>{fechaHora(cliente.fechas?.createdAtMigracion)}</dd>
                </div>
                <div>
                  <dt>Última Modificación</dt>
                  <dd>{fechaHora(cliente.fechas?.updatedAt)}</dd>
                </div>
                <div>
                  <dt>Creado Por</dt>
                  <dd className="text-muted" style={{ fontSize: '0.8125rem' }}>{cliente.auditoria?.createdBy}</dd>
                </div>
                <div>
                  <dt>Cuenta Contable Capital</dt>
                  <dd className="text-muted">{cliente.auditoria?.cuentaContableCapital || '—'}</dd>
                </div>
                <div>
                  <dt>Cuenta Contable Interés</dt>
                  <dd className="text-muted">{cliente.auditoria?.cuentaContableInteres || '—'}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cambio de Estado */}
      {dialogo && (
        <EstadoDialog
          destino={dialogo.destino}
          aplicando={aplicando}
          onConfirm={aplicarEstado}
          onCancel={() => setDialogo(null)}
        />
      )}
    </div>
  );
}

export default ClienteExpediente;
